import { ActivityIndicator, Linking, Text, View } from 'react-native'
import { useEffect, useState } from 'react'
import { CameraView, useCameraPermissions } from 'expo-camera'
import { colors } from '@chillout/core'
import { Button } from '@chillout/ui'
import { reportUnexpectedError } from '../../../lib/reportError'
import { Viewfinder } from './Viewfinder'

interface QRScannerStrings {
  permissionDescription: string
  permissionDeniedDescription: string
  permissionButton: string
  settingsButton: string
}

interface QRScannerProps {
  onScanned: (data: string) => void
  onReady?: () => void
  active?: boolean
  strings: QRScannerStrings
}

const CAMERA_WARMUP_MS = 500

export function QRScanner({ onScanned, onReady, active = true, strings }: QRScannerProps) {
  const [permission, requestPermission] = useCameraPermissions()
  const [overlayVisible, setOverlayVisible] = useState(false)

  useEffect(() => {
    if (!active) {
      setOverlayVisible(true)
      return
    }
    const timer = setTimeout(() => setOverlayVisible(false), CAMERA_WARMUP_MS)
    return () => clearTimeout(timer)
  }, [active])

  if (!permission) {
    return (
      <View className="flex-1 items-center justify-center rounded-2xl bg-canvas-dark">
        <ActivityIndicator size="small" color={colors.text.inverse} />
      </View>
    )
  }

  if (!permission.granted) {
    const canAskAgain = permission.canAskAgain
    return (
      <View className="flex-1 items-center justify-center rounded-2xl bg-canvas-dark px-6">
        <Text className="mb-4 text-center text-sm text-white">
          {canAskAgain ? strings.permissionDescription : strings.permissionDeniedDescription}
        </Text>
        <Button
          label={canAskAgain ? strings.permissionButton : strings.settingsButton}
          onPress={canAskAgain
            ? () => { requestPermission().catch(reportUnexpectedError) }
            : () => { void Linking.openSettings() }}
        />
      </View>
    )
  }

  return (
    <View className="flex-1 overflow-hidden rounded-2xl bg-canvas-dark">
      <CameraView
        style={{ flex: 1 }}
        facing="back"
        active={active}
        barcodeScannerSettings={{ barcodeTypes: ['qr'] }}
        onCameraReady={onReady}
        onMountError={(event) => reportUnexpectedError(new Error(event.message))}
        onBarcodeScanned={active ? ({ data }) => onScanned(data) : undefined}
      />
      <View className="absolute inset-0">
        <Viewfinder transparent />
      </View>
      {overlayVisible && <View className="absolute inset-0 bg-canvas-dark" />}
    </View>
  )
}